export interface ProjectWritableFileStream {
  write(data: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort?(): Promise<void>;
}

export interface ProjectFileHandle {
  readonly kind: 'file';
  readonly name: string;
  getFile(): Promise<File>;
  createWritable(): Promise<ProjectWritableFileStream>;
  queryPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
  requestPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
}

export interface FileSystemAccessBrowser extends Window {
  showOpenFilePicker?: (options?: ProjectOpenPickerOptions) => Promise<ProjectFileHandle[]>;
  showSaveFilePicker?: (options?: ProjectSavePickerOptions) => Promise<ProjectFileHandle>;
}

interface ProjectFileType {
  readonly description: string;
  readonly accept: Readonly<Record<string, readonly string[]>>;
}

interface ProjectOpenPickerOptions {
  readonly multiple?: boolean;
  readonly excludeAcceptAllOption?: boolean;
  readonly types?: readonly ProjectFileType[];
}

interface ProjectSavePickerOptions {
  readonly suggestedName?: string;
  readonly excludeAcceptAllOption?: boolean;
  readonly types?: readonly ProjectFileType[];
}

const projectTypes: readonly ProjectFileType[] = [{
  description: 'CASUR GeoStudio Project',
  accept: { 'application/zip': ['.casurgeo'] },
}];

export const supportsProjectOpenPicker = (browser: FileSystemAccessBrowser): boolean => typeof browser.showOpenFilePicker === 'function';
export const supportsProjectSavePicker = (browser: FileSystemAccessBrowser): boolean => typeof browser.showSaveFilePicker === 'function';

export const pickProjectToOpen = async (browser: FileSystemAccessBrowser): Promise<ProjectFileHandle | null> => {
  if (!browser.showOpenFilePicker) return null;
  const handles = await browser.showOpenFilePicker({ multiple: false, excludeAcceptAllOption: true, types: projectTypes });
  return handles[0] ?? null;
};

export const pickProjectToSave = async (browser: FileSystemAccessBrowser, suggestedName: string): Promise<ProjectFileHandle | null> => {
  if (!browser.showSaveFilePicker) return null;
  return browser.showSaveFilePicker({ suggestedName, excludeAcceptAllOption: true, types: projectTypes });
};

export const ensureProjectPermission = async (handle: ProjectFileHandle, mode: 'read' | 'readwrite'): Promise<boolean> => {
  if (!handle.queryPermission) return true;
  if (await handle.queryPermission({ mode }) === 'granted') return true;
  if (!handle.requestPermission) return false;
  return await handle.requestPermission({ mode }) === 'granted';
};

export const writeProjectBytes = async (handle: ProjectFileHandle, bytes: Uint8Array): Promise<boolean> => {
  if (!await ensureProjectPermission(handle, 'readwrite')) return false;
  const writable = await handle.createWritable();
  try {
    await writable.write(bytes);
    await writable.close();
    return true;
  } catch (error) {
    try { await writable.abort?.(); } catch { /* Preserve the original write error. */ }
    throw error;
  }
};

export const isPickerCancellation = (error: unknown): boolean => (
  typeof error === 'object' && error !== null && 'name' in error && (error as { name?: unknown }).name === 'AbortError'
);
